import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { getAuthErrorMessage } from '@/lib/auth-errors';

export function UpdatePasswordForm() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)

  useEffect(() => {
    // setLoading(true); // Already true from initial state
    let timeoutId: ReturnType<typeof setTimeout> | null = null; // Declare timeoutId here with proper type

    // 1. Check for query parameters and redirect if necessary
    const params = new URLSearchParams(window.location.search);
    if (params.has('access_token')) {
      // Construct the hash from query params
      const hashParams = [
        'access_token', 'refresh_token', 'expires_in',
        'expires_at', 'token_type', 'type'
      ]
        .map(key => params.has(key) ? `${key}=${encodeURIComponent(params.get(key)!)}` : null)
        .filter(Boolean)
        .join('&');
      // Redirect to the same page with the hash
      window.location.replace(`${window.location.pathname}#${hashParams}`);
      // Return early, the effect will re-run after redirection with the hash
      return;
    }

    // 2. If no query params, proceed. Check if a hash likely indicates a recovery attempt.
    let recoveryAttemptExpected = false;
    if (window.location.hash && window.location.hash.includes('access_token')) {
      recoveryAttemptExpected = true;
      // Clean the hash from the URL bar visually *after* Supabase has had a chance to process it.
      // Doing it here might be too early, let's try doing it in the listener or rely on Supabase handling it.
      // window.history.replaceState(null, '', window.location.pathname);
    }

    // 3. Set up the auth state listener
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        // Successfully detected the recovery session
        // Use functional updates to ensure we have the latest state
        setSessionReady(() => true);
        setLoading(() => false);

        window.history.replaceState(null, '', window.location.pathname);

      } else if (['INITIAL_SESSION', 'SIGNED_IN', 'SIGNED_OUT'].includes(event)) {
        // Use functional updates to get the latest state before deciding
        setLoading(currentLoading => {
          let shouldSetLoadingFalse = false;
          setSessionReady(currentSessionReady => {
            if (!currentSessionReady && currentLoading) {
              shouldSetLoadingFalse = true;
              // Clean hash here too, in case recovery failed but hash was present
              if (window.location.hash.includes('access_token')) {
                window.history.replaceState(null, '', window.location.pathname);
              }
            }
            // Return currentSessionReady unmodified as we only potentially change loading here
            return currentSessionReady;
          });
          // Return the new loading state based on the check inside setSessionReady's callback
          return shouldSetLoadingFalse ? false : currentLoading;
        });
      }
    });

    // 4. Initial loading state decision based on expected recovery attempt
    // Needs to run *after* listener is set up.
    if (!recoveryAttemptExpected) {
      // No recovery attempt was indicated by the hash, stop loading.
      if (loading) {
        setLoading(false);
      }
    } else {
      // A recovery attempt *was* expected. Keep loading = true.
      // We rely on the listener to set loading = false eventually.
      // Add a potential timeout as a fallback?
      timeoutId = setTimeout(() => { // Assign to the outer scope variable
        if (loading && !sessionReady) {
          setLoading(false);
        }
      }, 7000); // Increased timeout: 7 seconds
    }

    // 4. Check active session immediately on mount (handles PKCE flow where session attempts auto-refresh)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSessionReady(true)
        setLoading(false)
      } else {
        // If no immediate session, wait for listener
        if (!recoveryAttemptExpected) {
          setLoading(false)
        }
      }
    })

    // 5. Cleanup listener on component unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const { error } = await supabase.auth.updateUser({
        password,
      })

      if (error) throw error

      navigate('/')
    } catch (error) {
      setError(getAuthErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div>Chargement de la session...</div>
  }

  if (!sessionReady) {
    return (
      <div className="flex flex-col items-center justify-center p-4 text-center">
        <p className="mb-4 text-muted-foreground">Impossible de valider la session de modification.</p>
        <Button onClick={() => navigate('/auth/login')}>Retour à la connexion</Button>
      </div>
    )
  }


  return (
    <Card className="w-[400px]">
      <CardHeader>
        <CardTitle>Mettre à jour le mot de passe</CardTitle>
        <CardDescription>
          Choisissez un nouveau mot de passe pour votre compte
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">Nouveau mot de passe</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
} 